import { useUser } from '@app/context/UserContext';
import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useDashboard } from '@app/routes/Dashboard/useDashboard';
import { copyToClipboard } from '@app/utils/copyToClipboard';
import { Button, NotificationModal, Spinner, ThreeDots } from '@components/ui';
import { Icon } from '@components/ui/Icon/Icon';
import { ThreeDotItem } from '@components/ui/ThreeDots/ThreeDots';
import { useOpenQuestionnaireMutation } from '@dieterApi/questionnaire/useOpenQuestionnaireMutation';
import { useGetQuestionnaireApiDocQuery } from '@dieterApi/questionnaire/useQuestionnaireApiDocQuery';
import {
  ConsentFrom,
  useQuestionnaireDocumentShareQuery,
} from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';
import { useGetQuestionnaireHtmlQuery } from '@dieterApi/questionnaire/useQuestionnaireHtmlQuery';
import { ApplicationIDs } from '@dieterApi/user/useUserQuery';
import CodeIcon from '@mui/icons-material/Code';
import DataArrayIcon from '@mui/icons-material/DataArray';
import ExtensionIcon from '@mui/icons-material/Extension';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LinkIcon from '@mui/icons-material/Link';
import ShareIcon from '@mui/icons-material/Share';
import { Menu, MenuItem, ModalProps, Skeleton, Tooltip } from '@mui/material';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import LangSelect from '../Header/LangSelect';
interface Props {
  questionnaireId: string;
  documentNumber?: number;
}

export function ConsumerPanel({ questionnaireId, documentNumber = 1 }: Props) {
  // hooks
  const { t, i18n } = useTranslation();
  const { navigation } = useNavigation();
  const dashboard = useDashboard();
  const { user } = useUser();
  const { userValues } = useUserValuesData();
  const { enqueueSnackbar } = useSnackbar();
  const [openQuestionnaire, { data: questionnaireData, loading: questionnaireLoading }] =
    useOpenQuestionnaireMutation();
  const [getShareToken, { data: shareToken, loading: shareTokenLoading }] = useQuestionnaireDocumentShareQuery();
  const [getApiDoc, { data: apiDocData, loading: apiDocLoading }] = useGetQuestionnaireApiDocQuery();
  const apiDocId = apiDocData?.getQuestionnaireApiDoc;

  // states
  const [hasCopied, setHasCopied] = useState(false);
  const [chunkModalOpen, setChunkModalOpen] = useState(false);
  const [pluginModalOpen, setPluginModalOpen] = useState(false);
  const [htmlCopyModalOpen, setHtmlCopyModalOpen] = useState(false);
  const [downloadType, setDownloadType] = useState<'pdf' | 'docx' | 'html' | null>('pdf');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const questionnaire = questionnaireData?.openQuestionnaire;
  const consentFrom = (userValues?.find((uv) => uv.key === 'CONSENT_FROM' && uv.index === questionnaire?.userValueIndex)
    ?.value || '') as ConsentFrom;

  const tags = questionnaire?.application.tags?.map((tag) => tag.tag);
  const html = tags?.includes('html');
  const pdf = tags?.includes('pdf');
  const docx = tags?.includes('docx');
  const consentlink = tags?.includes('consentlink');
  const loading = questionnaireLoading || shareTokenLoading;

  useEffect(() => {
    openQuestionnaire({
      variables: {
        localQuestionnaireId: questionnaireId,
      },
    }).catch((error) => {
      console.log(error);
    });
  }, []);

  useEffect(() => {
    if (html) {
      if (!questionnaireId) return;
      getApiDoc({
        variables: {
          id: questionnaireId,
          locale: user?.locale,
        },
      });
    }
  }, [html, questionnaireId, user?.locale]);

  useEffect(() => {
    if (consentlink) {
      getShareToken({
        variables: {
          localQuestionnaireId: questionnaireId,
          type: 'ConsentForm',
          consentFrom,
          withConfirmation: true,
        },
      });
    }
  }, [consentlink]);

  if (!questionnaire) return <div className={`h-12 w-full bg-gray-200 animate-pulse`} />;

  const fileName = user?.company.name + ' ' + questionnaire.application.documentLabels[documentNumber - 1];

  const handleDownload = (type: 'docx' | 'pdf') => {
    setDownloadType(type);
    dashboard.download(questionnaire.id, fileName, type);
  };

  const isDownloading = dashboard.affects('download', questionnaire.id);

  const handlePrint = () => {
    const path = `/print/${questionnaire.id}${
      questionnaire.application.splitDocument ? '?documentNumber=' + documentNumber : ''
    }`;
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleCopyHtml = () => {
    setHtmlCopyModalOpen(true);
  };

  const handleCopyLink = () => {
    const rootPath = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;
    const locale = i18n.language.slice(0, 2);
    const url = rootPath + `/api/livedoc/${apiDocId}?locale=${locale}`;

    url &&
      copyToClipboard(url)
        .then(() => {
          setHasCopied(true);
          if (navigation.isMobile) {
            enqueueSnackbar(t('dashboard.topic.process.publishprocess.copy_success'), { variant: 'success' });
          }
        })
        .catch((error) => {
          enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
        });
  };

  const handleCopyConsentLink = () => {
    const url = shareToken ? window.location.origin + '/share?token=' + shareToken.getQuestionnaireShareToken : '';
    url &&
      copyToClipboard(url)
        .then(() => {
          setHasCopied(true);
          if (navigation.isMobile) {
            enqueueSnackbar(t('dashboard.topic.process.publishprocess.copy_success'), { variant: 'success' });
          }
        })
        .catch((error) => {
          enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
        });
  };

  const handleMobileClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const threeDotItems: ThreeDotItem[] = [
    {
      label: 'HTML Chunks',
      onClick: () => setChunkModalOpen(true),
      icon: <DataArrayIcon fontSize="small" />,
      tooltip: t('components.block.OutputView.ConsumerPanel.cmshint'),
    },
    {
      label: 'Wordpress',
      // open new window with link to plugin directory https://wordpress.org/plugins/dieter-privacy/
      onClick: () => window.open('https://wordpress.org/plugins/dieter-privacy/', '_blank'),
      icon: <WordpressIcon />,
      tooltip: t('components.block.OutputView.ConsumerPanel.wordpresshint'),
    },
    {
      label: 'Wix',
      onClick: () => window.open('https://de.wix.com/app-market/dieter-privacy', '_blank'),
      icon: <WixIcon />,
      tooltip: t('components.block.OutputView.ConsumerPanel.wixhint'),
    },
    {
      label: 'Shopify',
      onClick: () => window.open('https://apps.shopify.com/dieter-privacy', '_blank'),
      icon: <ShopifyIcon />,
      tooltip: t('components.block.OutputView.ConsumerPanel.shopifyhint'),
    },
  ];

  const apiDocLabels: Partial<Record<ApplicationIDs, string>> = {
    'app-dse-privacy-policy-create': 'dse',
    'app-dse-imprint-create': 'imprint',
    'app-social-facebook': 'dse-facebook',
    'app-social-instagram': 'dse-instagram',
    'app-social-linkedin': 'dse-linkedin',
    'app-social-tiktok': 'dse-tiktok',
  };

  return (
    <div className="bg-gray-300 w-full py-2 flex px-2 gap-4 justify-between  relative">
      {html && (
        <div className=" absolute text-xs flex gap-2 items-center bg-secondary-200 px-2 rounded-md right-0 -bottom-5">
          <span className="text-gray-400">ID:</span>{' '}
          {apiDocId ? (
            <span className="cursor-text z-50 text-gray-600">{apiDocId}</span>
          ) : (
            <Skeleton height="15px" width="150px" />
          )}
        </div>
      )}
      {/* <div className="flex-1" /> */}
      <button
        className="flex md:hidden gap-1 items-center underline text-primary-root hover:text-primary-700"
        onClick={handleMobileClick}
        data-testid="use-document-button"
      >
        <span>{t('components.block.OutputView.ConsumerPanel.usedocument')}</span>{' '}
        <KeyboardArrowDownIcon fontSize="inherit" />
        {isDownloading && <Spinner size="small" color="primary" />}
      </button>

      <div className="flex-10 gap-3 flex-wrap items-center justify-between hidden md:flex">
        {pdf && (
          <LinkWithIcon
            onClick={() => handleDownload('pdf')}
            label=".pdf"
            icon={<Icon type="pdf-16" size={20} />}
            spinner={isDownloading && downloadType === 'pdf'}
            data-testid="consumer-link-pdf"
          />
        )}
        {docx && (
          <LinkWithIcon
            onClick={() => handleDownload('docx')}
            label=".docx"
            icon={<Icon type="doc-16" size={20} />}
            spinner={isDownloading && downloadType === 'docx'}
            data-testid="consumer-link-docx"
          />
        )}
        {shareToken && (
          <Tooltip
            enterTouchDelay={0}
            leaveTouchDelay={5000}
            title={
              hasCopied
                ? t('dashboard.topic.process.publishprocess.copied')
                : t('components.block.OutputView.ConsumerPanel.consentlink')
            }
          >
            <div>
              <LinkWithIcon
                onClick={() => handleCopyConsentLink()}
                label="Link"
                icon={<ShareIcon />}
                data-testid="consumer-link-consentlink"
              />
            </div>
          </Tooltip>
        )}
        {html && (
          <div className="flex-1">
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              title={
                hasCopied
                  ? t('dashboard.topic.process.publishprocess.copied')
                  : t('components.block.OutputView.ConsumerPanel.linkcopied')
              }
            >
              <div>
                <LinkWithIcon
                  onClick={handleCopyLink}
                  onMouseLeave={() => setTimeout(() => setHasCopied(false), 1000)}
                  label="Link"
                  icon={<LinkIcon fontSize="small" />}
                  disabled={apiDocLoading || !apiDocId}
                  spinner={apiDocLoading}
                  data-testid="consumer-link-link"
                />
              </div>
            </Tooltip>
          </div>
        )}
        {html && (
          <div className="flex-1">
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              title={
                hasCopied
                  ? t('dashboard.topic.process.publishprocess.copied')
                  : t('components.block.OutputView.ConsumerPanel.htmlcopied')
              }
            >
              <div>
                <LinkWithIcon
                  onClick={handleCopyHtml}
                  onMouseLeave={() => setTimeout(() => setHasCopied(false), 1000)}
                  label="HTML"
                  icon={<CodeIcon fontSize="small" />}
                  data-testid="consumer-link-html"
                />
              </div>
            </Tooltip>
          </div>
        )}

        {html && (
          <div className="flex-1">
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              title={t('components.block.OutputView.ConsumerPanel.apihint')}
            >
              <div>
                <HighlightBadge>
                  <LinkWithIcon
                    onClick={() => setPluginModalOpen(true)}
                    label="API"
                    data-testid="consumer-link-wordpress"
                    icon={<ExtensionIcon fontSize="small" />}
                    disabled={apiDocLoading || !apiDocId}
                    spinner={apiDocLoading}
                  />
                </HighlightBadge>
              </div>
            </Tooltip>
          </div>
        )}
        {html && <ThreeDots items={threeDotItems} />}
        <LinkWithIcon
          onClick={handlePrint}
          label={t('common.print')}
          data-testid="consumer-link-print"
          icon={<Icon type="print-24" size={20} />}
        />
      </div>
      <LangSelect inHeader={false} />
      {/* <div className="flex-1" /> */}
      <ChunkModal open={chunkModalOpen} onClose={() => setChunkModalOpen(false)} questionnaireId={questionnaireId} />
      <HtmlCopyModal
        open={htmlCopyModalOpen}
        onClose={() => setHtmlCopyModalOpen(false)}
        questionnaireId={questionnaireId}
      />
      <PluginModal
        open={pluginModalOpen}
        onClose={() => setPluginModalOpen(false)}
        questionnaireId={questionnaireId}
        apiDocId={apiDocId}
        docLabel={apiDocLabels[questionnaire.application.id] || 'dse'}
      />
      <Menu
        className=""
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {pdf && (
          <MenuItem data-testid="consumer-link-pdf" onClick={() => handleDownload('pdf')}>
            .pdf
          </MenuItem>
        )}
        {docx && (
          <MenuItem data-testid="consumer-link-docx" onClick={() => handleDownload('docx')}>
            .docx
          </MenuItem>
        )}
        {shareToken && (
          <MenuItem data-testid="consumer-link-consentlink" onClick={() => handleCopyConsentLink()}>
            Link
          </MenuItem>
        )}
        {html && (
          <MenuItem
            data-testid="consumer-link-html"
            onClick={() => handleCopyHtml()}
          >
            HTML
          </MenuItem>
        )}
        {html && (
          <MenuItem data-testid="consumer-link-chunks" onClick={() => setChunkModalOpen(true)}>
            HTML Chunks
          </MenuItem>
        )}
        {html && (
          <MenuItem
            data-testid="consumer-link-wordpress"
            disabled={apiDocLoading || !apiDocId}
            onClick={() => setPluginModalOpen(true)}
          >
            <HighlightBadge inline={true}>
              <span>{apiDocLoading ? <Spinner size="small" color="primary" /> : 'API'}</span>
            </HighlightBadge>
          </MenuItem>
        )}
        <MenuItem data-testid="consumer-link-print" onClick={handlePrint}>
          {t('common.print')}
        </MenuItem>
      </Menu>
    </div>
  );
}

interface LinkWithIconProps extends React.HTMLAttributes<HTMLButtonElement> {
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  spinner?: boolean;
}
function LinkWithIcon({ onClick, label, icon, disabled, spinner = false, ...props }: LinkWithIconProps) {
  return (
    <div className="flex-1">
      <button
        disabled={disabled}
        onClick={onClick}
        className={cx(
          'h-full w-full flex items-center justify-center text-primary-root gap-2',
          !disabled && 'hover:text-primary-800 underline'
        )}
        {...props}
      >
        {spinner ? <Spinner size="small" color="primary" /> : icon}

        <span className="break-keep">{label}</span>
      </button>
    </div>
  );
}

interface ChunkModalProps {
  open: boolean;
  onClose: ModalProps['onClose'];
  questionnaireId: string;
}

function ChunkModal({ open, onClose, questionnaireId }: ChunkModalProps) {
  const { t } = useTranslation();
  const { navigation } = useNavigation();
  const [chunkSize, setChunkSize] = useState(10000);
  const [getHtml, { data: htmlData, loading: htmlLoading }] = useGetQuestionnaireHtmlQuery();
  const chunks = htmlData?.getQuestionnaireHtml;
  const [hasCopied, setHasCopied] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = () => {
    getHtml({ variables: { id: questionnaireId, chunkSize } });
  };

  const handleCopy = (chunk: string) => {
    copyToClipboard(chunk)
      .then(() => {
        setHasCopied(true);
        if (navigation.isMobile) {
          enqueueSnackbar(t('dashboard.topic.process.publishprocess.copy_success'), { variant: 'success' });
        }
      })
      .catch((error) => {
        enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
      });
  };

  return (
    <NotificationModal title="HTML Chunks" open={open} onClose={onClose}>
      <div className="flex flex-col gap-2 p-9">
        <h1 className="text">HTML Chunks</h1>
        <p>{t('components.block.OutputView.ConsumerPanel.cmshint')}</p>
        {/* an input field for the chunk size and a button to retrieve the chunks */}
        <div className="flex-col flex gap-2 md:flex-row">
          <label htmlFor="chunkSize">{t('components.block.OutputView.ConsumerPanel.maxchars')}</label>
          <input
            id="chunkSize"
            className="border border-gray-300 rounded-md p-2"
            type="number"
            data-testid="input-chunk-size"
            value={chunkSize}
            onChange={(e) => setChunkSize(parseInt(e.target.value))}
          />

          <Button onClick={handleClick} data-testid="button-get-chunks">
            {t('components.block.OutputView.ConsumerPanel.retrievchunks')}
          </Button>
        </div>
        {/* a list of the chunks */}
        {htmlLoading && <Spinner />}
        {chunks && (
          <table className="table-auto">
            <thead>
              <tr>
                <th>Chunk</th>
                <th>{t('components.block.OutputView.ConsumerPanel.characters')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {chunks?.map((chunk, index) => {
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{chunk.length}</td>
                    <td>
                      <Tooltip
                        enterTouchDelay={0}
                        leaveTouchDelay={5000}
                        title={hasCopied ? 'Kopiert!' : 'HTML in die Zwischenablage kopieren'}
                      >
                        <div>
                          <button
                            type="button"
                            className="underline text-primary-root hover:text-primary-800"
                            onClick={() => handleCopy(chunk)}
                            onMouseLeave={() => setTimeout(() => setHasCopied(false), 1000)}
                            data-testid={'button-copy-chunk-' + index}
                          >
                            {t('components.block.OutputView.ConsumerPanel.copy')}
                          </button>
                        </div>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </NotificationModal>
  );
}

interface PluginModalProps {
  open: boolean;
  onClose: ModalProps['onClose'];
  questionnaireId: string;
  apiDocId?: string;
  docLabel: string;
}

// function PluginModal({ open, onClose, questionnaireId }: PluginModalProps) {
//   const { t } = useTranslation();
//   const [getToken, { data: tokenData, loading: tokenLoading }] = useQuestionnaireDocumentShareQuery();
//   const token = tokenData?.getQuestionnaireShareToken;
//   const [hasCopied, setHasCopied] = useState(false);
//   const { enqueueSnackbar } = useSnackbar();

//   useEffect(() => {
//     if (!questionnaireId) return;
//     getToken({
//       variables: {
//         localQuestionnaireId: questionnaireId,
//         type: 'Document',
//       },
//     });
//   }, [questionnaireId]);

//   const handleCopyClick = () => {
//     if (!token) return;
//     copyToClipboard(token)
//       .then(() => {
//         setHasCopied(true);
//       })
//       .catch((error) => {
//         enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
//       });
//   };

//   return (
//     <NotificationModal title="HTML Chunks" open={open} onClose={onClose}>
//       <div className="flex flex-col gap-2 p-9">
//         <h1 className="text">Wordpress Plugin</h1>
//         <p>
//           <Trans t={t} i18nKey={'components.block.OutputView.ConsumerPanel.plugintoken'} values={{ token }}>
//             Binde das Dokument mit Hilfe unserer API direkt in Wordpress ein. Du kannst das Dokument dann über den
//             Shortcode <code>[dieter doc="{token}"]</code> in deinen Wordpress-Beitrag einbinden.
//           </Trans>
//         </p>
//         <p>
//           <a href="https://dieterfilestorage.blob.core.windows.net/public/dieter-privacy-wordpress.zip">
//             {t('components.block.OutputView.ConsumerPanel.install')}
//           </a>
//         </p>
//         <label htmlFor="token" className="text-sm font-semibold">
//           Document ID
//         </label>
//         <div className="flex">
//           <div className="flex-1">
//             <Input
//               id="token"
//               type="text"
//               value={token}
//               onFocus={(e) => e.target.select()}
//               textSize={'small'}
//               readOnly={true}
//               data-testid={'input-share-url'}
//             />
//           </div>
//           <div>
//             <Tooltip
//               enterTouchDelay={0}
//               leaveTouchDelay={5000}
//               title={hasCopied ? 'Kopiert!' : 'In Zwischenablage kopieren'}
//             >
//               <MuiButton
//                 onClick={handleCopyClick}
//                 onMouseLeave={() => setTimeout(() => setHasCopied(false), 1000)}
//                 data-testid={'button-copy-clipboard'}
//               >
//                 <ContentCopyIcon />
//               </MuiButton>
//             </Tooltip>
//           </div>
//         </div>
//       </div>
//     </NotificationModal>
//   );
// }

function PluginModal({ open, onClose, apiDocId, docLabel }: PluginModalProps) {
  const { t, i18n } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [noTitle, setNoTitle] = useState(false);
  const [noStyles, setNoStyles] = useState(false);

  const anchorSnippet = `<!-- Dieter Document Anchor -->
<div id="dieter-${docLabel}"></div>`;

  const livedocUrl = `${window.location.origin}/api/livedoc/${apiDocId}?locale=${i18n.language.slice(0, 2)}${
    noStyles ? '&unstyled=true' : ''
  }`;

  const noScriptSnippet = `
  <noscript>
    <p>${t('components.block.OutputView.ConsumerPanel.apimodal.noscript1')}:</p>
    <a href="${livedocUrl}" rel="nofollow noopener" target="_blank">${t('common.cta.show_document')}</a>
  </noscript>
  `;

  const fullSnippet = apiDocId
    ? `<!-- Dieter Document API -->
<link rel="stylesheet" href="https://script.simply-legal.tech/dieter-documents.css">
<script src="https://script.simply-legal.tech/dieter-documents.js"
    data-document-id="${apiDocId}"
    data-api-host="${window.location.origin}"
    data-target-selector="#dieter-${docLabel}"
    data-gutter-width="1.5cm"
    data-locale="${i18n.language.slice(0, 2)}"
    data-no-title="${noTitle}"
    data-no-styles="${noStyles}">
</script>` +
      noScriptSnippet +
      anchorSnippet
    : 'Loading...';

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto'; // Reset height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set to scroll height
    }
  }, [fullSnippet]); // Dependency array includes `fullSnippet` to re-apply effect when it changes

  return (
    <NotificationModal title="HTML Chunks" open={open} onClose={onClose}>
      <div className="flex flex-col gap-4 p-9">
        <h1 className="text">{t('components.block.OutputView.ConsumerPanel.apimodal.title')}</h1>
        <p>{t('components.block.OutputView.ConsumerPanel.apimodal.description')}</p>
        <Trans
          t={t}
          i18nKey={'components.block.OutputView.ConsumerPanel.apimodal.snippet2'}
          values={{ body: '<body/>' }}
        >
          <span>
            Übertrage diesen Code-Abschnitt in den{' '}
            <span className="font-mono text-sm text-primary-root">{'<body/>'}</span> deiner Webseite an die Stelle an
            die das Dokument eingefügt werden soll:
          </span>
        </Trans>
        <div className="flex flex-col gap-2 bg-gray-50 border border-gray-200 rounded-md p-3">
          <span className="font-semibold text-sm">
            {t('components.block.OutputView.ConsumerPanel.apimodal.optionstitle')}
          </span>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={noTitle}
              onChange={(e) => setNoTitle(e.target.checked)}
              className="mt-1"
              data-testid="api-toggle-no-title"
            />
            <span>
              <code className="text-xs">data-no-title</code> —{' '}
              {t('components.block.OutputView.ConsumerPanel.apimodal.param6')}
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={noStyles}
              onChange={(e) => setNoStyles(e.target.checked)}
              className="mt-1"
              data-testid="api-toggle-no-styles"
            />
            <span>
              <code className="text-xs">data-no-styles</code> —{' '}
              {t('components.block.OutputView.ConsumerPanel.apimodal.param4')}
            </span>
          </label>
        </div>
        <textarea
          ref={textareaRef}
          data-testid="api-snippet"
          readOnly
          className="w-full h-96 p-4 bg-gray-100 text-gray-800 font-mono text-xs border border-gray-300 rounded-md shadow-sm focus:ring-primary-root focus:outline-hidden overflow-y-auto"
          value={fullSnippet}
        />
        <span className="font-semibold">{t('components.block.OutputView.ConsumerPanel.apimodal.paramtitle')}</span>
        <ul className="pl-0">
          <li>
            <code>data-document-id</code>: {t('components.block.OutputView.ConsumerPanel.apimodal.param1')}
          </li>
          <li>
            <code>data-target-selector</code> {t('components.block.OutputView.ConsumerPanel.apimodal.param2')}
          </li>
          <li>
            <code>data-gutter-width</code> {t('components.block.OutputView.ConsumerPanel.apimodal.param3')}
          </li>
          <li>
            <code>data-locale</code> {t('components.block.OutputView.ConsumerPanel.apimodal.param5')}
          </li>
          <li>
            <code>data-no-title</code> {t('components.block.OutputView.ConsumerPanel.apimodal.param6')}
          </li>
          <li>
            <code>data-no-styles</code> {t('components.block.OutputView.ConsumerPanel.apimodal.param4')}
          </li>
        </ul>
        <div className="bg-secondary-100 border-l-4 border-secondary-500 text-secondary-700 p-4 mt-4">
          <p className="font-medium">{t('dashboard.consentmanagement.important')}:</p>
          {t('components.block.OutputView.ConsumerPanel.apimodal.disclaimer')}
        </div>
      </div>
    </NotificationModal>
  );
}

interface HtmlCopyModalProps {
  open: boolean;
  onClose: ModalProps['onClose'];
  questionnaireId: string;
}

function HtmlCopyModal({ open, onClose, questionnaireId }: HtmlCopyModalProps) {
  const { t } = useTranslation();
  const { navigation } = useNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const [unstyled, setUnstyled] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [getHtml, { loading: htmlLoading }] = useGetQuestionnaireHtmlQuery();

  // Reset copy feedback whenever the toggle flips so the user knows a fresh copy is needed.
  useEffect(() => {
    setHasCopied(false);
  }, [unstyled, open]);

  const handleCopy = async () => {
    try {
      const result = await getHtml({ variables: { id: questionnaireId, unstyled } });
      const html = result.data?.getQuestionnaireHtml?.[0];
      if (!html) {
        enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
        return;
      }
      await copyToClipboard(html);
      setHasCopied(true);
      if (navigation.isMobile) {
        enqueueSnackbar(t('dashboard.topic.process.publishprocess.copy_success'), { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar(t('components.block.OutputView.ConsumerPanel.copyerror'), { variant: 'error' });
    }
  };

  return (
    <NotificationModal title="HTML Copy" open={open} onClose={onClose}>
      <div className="flex flex-col gap-4 p-9">
        <h1 className="text">{t('components.block.OutputView.ConsumerPanel.htmlmodal.title')}</h1>
        <p>{t('components.block.OutputView.ConsumerPanel.htmlmodal.description')}</p>
        <label className="flex items-start gap-2 text-sm cursor-pointer bg-gray-50 border border-gray-200 rounded-md p-3">
          <input
            type="checkbox"
            checked={unstyled}
            onChange={(e) => setUnstyled(e.target.checked)}
            className="mt-1"
            data-testid="html-copy-toggle-unstyled"
          />
          <span>
            <code className="text-xs">unstyled</code> —{' '}
            {t('components.block.OutputView.ConsumerPanel.htmlmodal.unstyledhint')}
          </span>
        </label>
        <div className="flex gap-2 items-center">
          <Button onClick={handleCopy} disabled={htmlLoading} data-testid="button-copy-html">
            {htmlLoading ? <Spinner size="small" color="primary" /> : t('components.block.OutputView.ConsumerPanel.copy')}
          </Button>
          {hasCopied && (
            <span className="text-sm text-green-700">
              {t('dashboard.topic.process.publishprocess.copied')}
            </span>
          )}
        </div>
      </div>
    </NotificationModal>
  );
}

function HighlightBadge({ children, inline = false }: { children: JSX.Element; inline?: boolean }) {
  const { t } = useTranslation();
  if (inline) {
    return (
      <div className="flex gap-2 items-center">
        <div>{children}</div>
        <div className=" bg-red-200 text-xs text-red-600 rounded-full px-1 shadow-md">
          {t('components.ui.newbadge.new')}
        </div>
      </div>
    );
  } else {
    return (
      <div className="relative">
        <div className="">{children}</div>
        <div className="absolute -top-2 right-0 bg-red-200 text-xs text-red-600 rounded-full px-1 shadow-md">
          {t('components.ui.newbadge.new')}
        </div>
      </div>
    );
  }
}

const WordpressIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 50 50">
      <path d="M25,2C12.317,2,2,12.318,2,25s10.317,23,23,23s23-10.318,23-23S37.683,2,25,2z M25,7c4.26,0,8.166,1.485,11.247,3.955 c-0.956,0.636-1.547,1.74-1.547,2.945c0,1.6,0.9,3,1.9,4.6c0.8,1.3,1.6,3,1.6,5.4c0,1.7-0.5,3.8-1.5,6.4l-2,6.6l-7.1-21.2 c1.2-0.1,2.3-0.2,2.3-0.2c1-0.1,0.9-1.6-0.1-1.6c0,0,0,0-0.1,0c0,0-3.2,0.3-5.3,0.3c-1.9,0-5.2-0.3-5.2-0.3s0,0-0.1,0 c-1,0-1.1,1.6-0.1,1.6c0,0,1,0.1,2.1,0.2l3.1,8.4L19.9,37l-7.2-21.4c1.2-0.1,2.3-0.2,2.3-0.2c1-0.1,0.9-1.6-0.1-1.6c0,0,0,0-0.1,0 c0,0-2.152,0.202-4.085,0.274C14.003,9.78,19.168,7,25,7z M7,25c0-1.8,0.271-3.535,0.762-5.174l7.424,20.256 C10.261,36.871,7,31.323,7,25z M19.678,42.2L25,26.6l5.685,15.471C28.897,42.665,26.989,43,25,43 C23.147,43,21.36,42.719,19.678,42.2z M35.304,39.75L35.304,39.75L40.6,24.4c0.786-2,1.21-3.742,1.39-5.304 C42.633,20.947,43,22.928,43,25C43,31.111,39.954,36.497,35.304,39.75z"></path>
    </svg>
  );
};

const WixIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 32 32">
      <path d="M 22.050781 9.9492188 C 21.525781 9.9242187 21.099609 10 21.099609 10 L 25.099609 16 L 21.099609 21.900391 C 21.099609 21.900391 22.899219 22.200391 23.699219 21.400391 C 24.199219 20.900391 24.499219 20.4 26.199219 18 C 26.499219 17.5 26.800781 18 26.800781 18 C 28.300781 20 28.700391 20.800391 29.400391 21.400391 C 30.300391 22.100391 32 21.900391 32 21.900391 L 28 16 L 31.900391 10 C 31.900391 10 30.200391 9.7 29.400391 10.5 C 28.800391 11.1 28.200781 11.9 26.800781 14 C 26.800781 14 26.499219 14.5 26.199219 14 C 24.799219 12 24.199609 11.1 23.599609 10.5 C 23.199609 10.1 22.575781 9.9742188 22.050781 9.9492188 z M 0.41992188 9.9902344 C 0.27851562 9.9835938 0.1375 9.9875 0 10 L 3.1992188 22 C 3.1992187 22 4.1992187 22.000781 4.6992188 21.800781 C 5.3992187 21.500781 5.6996094 21.199609 6.0996094 19.599609 C 6.4996094 18.199609 7.4996094 13.899609 7.5996094 13.599609 C 7.7996094 12.899609 8.1007813 12.899609 8.3007812 13.599609 C 8.4007812 13.899609 9.4007812 18.099609 9.8007812 19.599609 C 10.200781 21.199609 10.499219 21.500781 11.199219 21.800781 C 11.799219 22.100781 12.800781 22 12.800781 22 L 16 10 C 14.9 9.9 13.6 10.499219 13.5 11.699219 L 11.800781 18.099609 L 10.400391 12.900391 C 10.100391 11.400391 9.3996094 10.599609 8.0996094 10.599609 C 6.7996094 10.599609 6.2007813 11.300391 5.8007812 12.900391 L 4.4003906 18.099609 L 2.6992188 11.699219 C 2.4367187 10.561719 1.4097656 10.036719 0.41992188 9.9902344 z M 19.599609 10 C 19.599609 10 18.800781 9.9992188 18.300781 10.199219 C 17.700781 10.499219 17.5 11.099609 17.5 12.599609 C 17.5 12.599609 17.800781 12.299609 18.300781 12.099609 C 19.700781 11.599609 19.599609 10.6 19.599609 10 z M 19.5 12 L 19.5 12.076172 C 19.524326 12.032657 19.530125 12 19.5 12 z M 19.5 12.076172 C 19.443576 12.177106 19.249766 12.36025 18.900391 12.5 C 18.600391 12.7 18.3 12.800391 18 12.900391 C 17.3 13.200391 17.400391 13.599609 17.400391 14.599609 L 17.400391 22 C 17.400391 22 18.199219 22.100781 18.699219 21.800781 C 19.399219 21.500781 19.5 21.099609 19.5 19.599609 L 19.5 12.076172 z"></path>
    </svg>
  );
};

const ShopifyIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="-18 0 292 292"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid"
    >
      <g>
        <path
          d="M223.773626,57.3402078 C223.572932,55.8793405 222.29409,55.0718963 221.236945,54.9832175 C220.182133,54.8945386 197.853734,53.2399781 197.853734,53.2399781 C197.853734,53.2399781 182.346604,37.8448639 180.64537,36.1412966 C178.941803,34.4377293 175.616346,34.9558004 174.325836,35.336186 C174.134476,35.3921937 170.937371,36.3793293 165.646977,38.0152206 C160.466266,23.1101737 151.325344,9.41162582 135.241802,9.41162582 C134.798408,9.41162582 134.341011,9.43029505 133.883615,9.45596525 C129.309654,3.40713457 123.643542,0.779440373 118.74987,0.779440373 C81.285392,0.779440373 63.3862673,47.6135387 57.7738299,71.414474 C43.2164974,75.9254268 32.8737437,79.1318671 31.5528956,79.5472575 C23.4271131,82.0956074 23.1704111,82.3523094 22.1039313,90.0090275 C21.2988208,95.8058236 0.0369009009,260.235071 0.0369009009,260.235071 L165.714653,291.277334 L255.485648,271.856667 C255.485648,271.856667 223.971987,58.8010751 223.773626,57.3402078 L223.773626,57.3402078 Z M156.48972,40.8482763 C152.328815,42.1364532 147.598499,43.5996542 142.471461,45.1865388 C142.476129,44.1994032 142.480796,43.2262696 142.480796,42.1644571 C142.480796,32.8998514 141.194953,25.4414939 139.132003,19.5280151 C147.418807,20.5688247 152.937899,29.9967861 156.48972,40.8482763 L156.48972,40.8482763 Z M128.852258,21.3646006 C131.155574,27.1380602 132.65378,35.4225312 132.65378,46.6030666 C132.65378,47.1748118 132.649112,47.6975503 132.644445,48.2272897 C123.52686,51.0510108 113.620499,54.1174319 103.690802,57.1931876 C109.265901,35.6768995 119.716003,25.2851391 128.852258,21.3646006 L128.852258,21.3646006 Z M117.720729,10.8281537 C119.337951,10.8281537 120.966841,11.3765623 122.525722,12.4500431 C110.519073,18.099819 97.6489725,32.3304399 92.2138928,60.7473424 C84.2701352,63.2070135 76.506069,65.6106769 69.3277499,67.834649 C75.6939575,46.1596724 90.8113669,10.8281537 117.720729,10.8281537 L117.720729,10.8281537 Z"
          fill="#95BF46"
        ></path>
        <path
          d="M221.236945,54.9832175 C220.182133,54.8945386 197.853734,53.2399781 197.853734,53.2399781 C197.853734,53.2399781 182.346604,37.8448639 180.64537,36.1412966 C180.008283,35.5065427 179.149498,35.1821649 178.251042,35.0421456 L165.723988,291.275001 L255.485648,271.856667 C255.485648,271.856667 223.971987,58.8010751 223.773626,57.3402078 C223.572932,55.8793405 222.29409,55.0718963 221.236945,54.9832175"
          fill="#5E8E3E"
        ></path>
        <path
          d="M135.241802,104.585029 L124.173282,137.510551 C124.173282,137.510551 114.474617,132.334507 102.586984,132.334507 C85.1592573,132.334507 84.2818035,143.272342 84.2818035,146.028387 C84.2818035,161.066452 123.48252,166.828244 123.48252,202.052414 C123.48252,229.764553 105.90544,247.610004 82.2048516,247.610004 C53.7646126,247.610004 39.2212821,229.90924 39.2212821,229.90924 L46.8359944,204.750118 C46.8359944,204.750118 61.7853808,217.585214 74.4011133,217.585214 C82.6435785,217.585214 85.9970391,211.095323 85.9970391,206.353338 C85.9970391,186.736644 53.8369559,185.861524 53.8369559,153.629098 C53.8369559,126.500372 73.3089633,100.246767 112.614694,100.246767 C127.760108,100.246767 135.241802,104.585029 135.241802,104.585029"
          fill="#FFFFFF"
        ></path>
      </g>
    </svg>
  );
};
